import React, { useEffect, useRef, useState } from "react";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";
import type { ResearchDocumentData } from "../types";

interface ResearchDocumentCardProps {
  doc: ResearchDocumentData;
}

const FORMAT_ORDER = ["pdf", "docx", "md"] as const;
const FORMAT_LABELS: Record<string, string> = { pdf: "PDF", docx: "Word (.docx)", md: "Markdown (.md)" };

export const ResearchDocumentCard: React.FC<ResearchDocumentCardProps> = ({ doc }) => {
  const wordCount = doc.char_count ? Math.round(doc.char_count / 5) : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Build the list of downloadable formats. Prefer doc.formats; fall back to the
  // single legacy url (md) so older documents still work.
  const downloads = (() => {
    const out: { fmt: string; label: string; url: string }[] = [];
    const fmts = doc.formats || {};
    for (const fmt of FORMAT_ORDER) {
      const url = fmts[fmt]?.url;
      if (url) out.push({ fmt, label: FORMAT_LABELS[fmt] || fmt.toUpperCase(), url });
    }
    if (out.length === 0 && doc.url) {
      out.push({ fmt: doc.fmt || "md", label: FORMAT_LABELS[doc.fmt] || `.${doc.fmt || "md"}`, url: doc.url });
    }
    return out;
  })();

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  return (
    <div className={`${CHAT_COLUMN_CLASS} animate-fadeIn`}>
      <div className="w-full min-w-0">
        <div className="rounded-2xl border border-theme-border bg-theme-surface-ui p-4 space-y-3">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-theme-accent/10">
              <svg className="h-5 w-5 text-theme-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-theme-fg leading-snug">{doc.title}</p>
              <p className="text-xs text-theme-fg-muted mt-0.5">
                Deep Research Report
                {wordCount ? ` · ~${wordCount.toLocaleString()} words` : ""}
                {doc.partial ? " · partial" : ""}
                {" · "}
                <span className="uppercase">.{doc.fmt}</span>
              </p>
            </div>
          </div>

          {/* Preview */}
          {doc.preview && (
            <p className="text-xs text-theme-fg-muted line-clamp-3 leading-relaxed pl-12">
              {doc.preview.replace(/^#+\s+[^\n]+\n?/, "").slice(0, 300)}
            </p>
          )}

          {/* Citations count */}
          {doc.citations && doc.citations.length > 0 && (
            <p className="pl-12 text-xs text-theme-fg-muted">
              {doc.citations.length} sources cited
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pl-12">
            {downloads.length > 0 ? (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                  <svg className={`h-3 w-3 transition-transform ${menuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {menuOpen && (
                  <div className="absolute left-0 z-10 mt-1 min-w-[160px] overflow-hidden rounded-lg border border-theme-border bg-theme-surface shadow-lg">
                    {downloads.map((d) => (
                      <a
                        key={d.fmt}
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        onClick={() => setMenuOpen(false)}
                        className="block px-3 py-2 text-xs text-theme-fg hover:bg-theme-surface-ui transition-colors"
                      >
                        {d.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg-muted border border-theme-border">
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Preparing download…
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
